import { createI18n } from 'vue-i18n'
import { computed } from 'vue'
import { SUPPORTED_LANG } from 'widget-components/consts'
import { createTLI18n as createComponentsTLI18n } from 'widget-components/utilities/i18n'


const locales: Record<string, SUPPORTED_LANG> = {
    'de-at': SUPPORTED_LANG.DE, // German     -  Austria
    'de-ch': SUPPORTED_LANG.DE, // German     -  Switzerland
    'de-de': SUPPORTED_LANG.DE, // German     -  Germany
    'de-li': SUPPORTED_LANG.DE, // German     -  Liechtenstein
    'de-lu': SUPPORTED_LANG.DE, // German     -  Luxembourg
    de: SUPPORTED_LANG.DE, // German
    'en-gb': SUPPORTED_LANG.EN, // English    -  United  Kingdom
    'en-us': SUPPORTED_LANG.EN, // English    -  United  States
    en: SUPPORTED_LANG.EN, // English
    'es-ar': SUPPORTED_LANG.ES, // Spanish    -  Argentina
    'es-cl': SUPPORTED_LANG.ES, // Spanish    -  Chile
    'es-co': SUPPORTED_LANG.ES, // Spanish    -  Colombia
    'es-ec': SUPPORTED_LANG.ES, // Spanish    -  Ecuador
    'es-es': SUPPORTED_LANG.ES, // Spanish    -  Spain
    'es-mx': SUPPORTED_LANG.ES, // Spanish    -  Mexico
    'es-pe': SUPPORTED_LANG.ES, // Spanish    -  Peru
    'es-pr': SUPPORTED_LANG.ES, // Spanish    -  Puerto  Rico
    'es-uy': SUPPORTED_LANG.ES, // Spanish    -  Uruguay
    'es-ve': SUPPORTED_LANG.ES, // Spanish    -  Venezuela
    es: SUPPORTED_LANG.ES, // Spanish
    fi: SUPPORTED_LANG.FI, // Finnish
    'fr-be': SUPPORTED_LANG.FR, // French     -  Belgium
    'fr-ca': SUPPORTED_LANG.FR, // French     -  Canada
    'fr-ch': SUPPORTED_LANG.FR, // French     -  Switzerland
    'fr-fr': SUPPORTED_LANG.FR, // French     -  France
    'fr-lu': SUPPORTED_LANG.FR, // French     -  Luxembourg
    'fr-mc': SUPPORTED_LANG.FR, // French     -  Monaco
    fr: SUPPORTED_LANG.FR, // French
    'it-ch': SUPPORTED_LANG.IT, // Italian    -  Switzerland
    'it-it': SUPPORTED_LANG.IT, // Italian    -  Italy
    it: SUPPORTED_LANG.IT, // Italian
    lt: SUPPORTED_LANG.LT, // Lithuanian
    'nl-be': SUPPORTED_LANG.NL, // Dutch      -  Belgium (Flemish)
    'nl-nl': SUPPORTED_LANG.NL, // Dutch      -  Netherlands
    nl: SUPPORTED_LANG.NL, // Dutch
    'pl-pl': SUPPORTED_LANG.PL, // Polish     -  Poland
    pl: SUPPORTED_LANG.PL, // Polish
    'pt-ao': SUPPORTED_LANG.PT, // Portuguese -  Angola
    'pt-br': SUPPORTED_LANG.PT, // Portuguese -  Brazil
    'pt-cv': SUPPORTED_LANG.PT, // Portuguese -  Cape    Verde
    'pt-mz': SUPPORTED_LANG.PT, // Portuguese -  Mozambique
    'pt-pt': SUPPORTED_LANG.PT, // Portuguese -  Portugal
    pt: SUPPORTED_LANG.PT, // Portuguese
    ua: SUPPORTED_LANG.UK, // Ukrainian
    'uk-ua': SUPPORTED_LANG.UK, // Ukrainian  -  Ukraine
    uk: SUPPORTED_LANG.UK, // Ukrainian  -  (alternative code)
    ru: SUPPORTED_LANG.RU,
    'ru-ru': SUPPORTED_LANG.RU,
    'ru-ua': SUPPORTED_LANG.RU,
    'ru-by': SUPPORTED_LANG.RU,
    'ru-kz': SUPPORTED_LANG.RU,
    'ru-kg': SUPPORTED_LANG.RU,
    'ru-md': SUPPORTED_LANG.RU,
    'ru-tj': SUPPORTED_LANG.RU,
    'ru-uz': SUPPORTED_LANG.RU,
    'ru-am': SUPPORTED_LANG.RU,
    'ru-az': SUPPORTED_LANG.RU,
    'ru-ee': SUPPORTED_LANG.RU,
    'ru-lv': SUPPORTED_LANG.RU,
    'ru-lt': SUPPORTED_LANG.RU,
    fallback: SUPPORTED_LANG.EN, // Fallback language
}

export class TLI18n {
    i18n

    constructor(lang: SUPPORTED_LANG) {
        this.i18n = createI18n({
            legacy: false,
            locale: lang,
            fallbackLocale: 'en',
        })
    }
}

let I18N_INSTANCE: null | TLI18n = null
let I18N_INSTANCE_WIDGET_INSTANCE: null | TLI18n = null

export async function createTLI18n(lang: string) {
    if (I18N_INSTANCE) {
        // eslint-disable-next-line no-console
        console.warn('i18n service already created')
        return I18N_INSTANCE.i18n
    }

    const pickedLocale: SUPPORTED_LANG = locales[lang.toLowerCase()] || locales.fallback

    I18N_INSTANCE = new TLI18n(pickedLocale)
    I18N_INSTANCE_WIDGET_INSTANCE = createComponentsTLI18n(pickedLocale) as TLI18n

    await loadTexts(pickedLocale)

    return I18N_INSTANCE.i18n
}

const loadTexts = async (lang: string) => {
    if (I18N_INSTANCE) {
        const localTexts = await import(`../../texts/${lang}.json`)
        I18N_INSTANCE.i18n.global.setLocaleMessage(lang, localTexts.default)
        return
    }
}

const changeLang = async (value?: string) => {
    if (I18N_INSTANCE && I18N_INSTANCE_WIDGET_INSTANCE && value) {
        const lang = locales[value.toLowerCase()]
        if (lang) {
            I18N_INSTANCE.i18n.global.locale.value = lang
            I18N_INSTANCE_WIDGET_INSTANCE.i18n.global.locale.value = lang
            await loadTexts(lang)
        } else {
            // eslint-disable-next-line no-console
            console.error(`${lang} lang not available, please use one of these: ${Object.values(SUPPORTED_LANG).join(', ')}`)
        }
    }
}

const getCurrentLocale = computed(() => {
    if (I18N_INSTANCE) {
        return locales[I18N_INSTANCE.i18n.global?.locale?.value?.toLowerCase()] || 'en'
    }

    return SUPPORTED_LANG.EN
})

export function useI18n() {
    if (!I18N_INSTANCE) {
        throw 'i18n haven\'t been created'
    }

    return Object.assign({ changeLang, getCurrentLocale }, I18N_INSTANCE.i18n.global)
}
